import type {
  SkillCatalogEntry,
  SkillCatalogSkill,
  SkillFacets,
  SkillSection,
  SkillsLibraryFilters,
} from "connected-components/skills/types"

const LICENSED_SECTION = { id: "omega-point", title: "Core" }
const PROPRIETARY_SECTION = { id: "client-specific", title: "Tailored" }
const USER_SECTION = { id: "yours", title: "Yours" }

// The gates that don't depend on chip selections: tab, the preview (beta) switch,
// and search. Shared by the visible-list filter and the facet helpers so chip
// availability and the rendered list always agree on what's "in scope".
type BaseFilters = Pick<SkillsLibraryFilters, "tab" | "query" | "showPreview">

function toSortedSkills(skills: SkillCatalogEntry[], tier: SkillCatalogSkill["tier"]): SkillCatalogSkill[] {
  return skills
    .map(skill => ({ ...skill, tier }))
    .sort((left, right) => left.name.localeCompare(right.name))
}

export function buildSkillSections(
  licensedSkills: SkillCatalogEntry[],
  proprietarySkills: SkillCatalogEntry[],
  userSkills: SkillCatalogEntry[] = [],
): SkillSection[] {
  const sections: SkillSection[] = []

  if (licensedSkills.length) {
    sections.push({ ...LICENSED_SECTION, skills: toSortedSkills(licensedSkills, "licensed") })
  }
  if (proprietarySkills.length) {
    sections.push({ ...PROPRIETARY_SECTION, skills: toSortedSkills(proprietarySkills, "proprietary") })
  }
  if (userSkills.length) {
    sections.push({ ...USER_SECTION, skills: toSortedSkills(userSkills, "user") })
  }

  return sections
}

function matchesQuery(skill: SkillCatalogSkill, normalizedQuery: string): boolean {
  return skill.name.toLowerCase().includes(normalizedQuery)
    || skill.description.toLowerCase().includes(normalizedQuery)
}

function passesBaseGates(skill: SkillCatalogSkill, { tab, showPreview }: Pick<SkillsLibraryFilters, "tab" | "showPreview">, normalizedQuery: string): boolean {
  if (tab === "tailored" && skill.tier !== "proprietary") {
    return false
  }
  if (tab === "core" && skill.tier !== "licensed") {
    return false
  }
  if (tab === "yours" && skill.tier !== "user") {
    return false
  }
  // Preview is opt-in: beta skills stay hidden until the switch is on.
  if (!showPreview && skill.beta) {
    return false
  }
  if (normalizedQuery && !matchesQuery(skill, normalizedQuery)) {
    return false
  }
  return true
}

// Role and category chips are OR'd together: with any chip selected, a skill
// qualifies if it carries any one of the selected roles or categories. With no
// chips selected, everything in scope qualifies.
function matchesSelectedChips(skill: SkillCatalogSkill, roles: string[], categories: string[]): boolean {
  if (!roles.length && !categories.length) {
    return true
  }
  if (skill.tier === "user") {
    return true
  }
  return roles.some(role => skill.roles?.includes(role))
    || categories.some(category => skill.categories?.includes(category))
}

export function filterSkillSections(
  sections: SkillSection[],
  { tab, query, roles, categories, showPreview }: SkillsLibraryFilters,
): SkillSection[] {
  const normalizedQuery = query.trim().toLowerCase()

  return sections
    .map(section => ({
      ...section,
      skills: section.skills.filter(skill =>
        passesBaseGates(skill, { tab, showPreview }, normalizedQuery)
        && matchesSelectedChips(skill, roles, categories)),
    }))
    .filter(section => section.skills.length > 0)
}

// Skills in scope before chip selection — the candidate set the chips filter and
// from which the available facet values are derived. Chips are deliberately
// excluded here: they OR together, so selecting one never narrows the others.
export function getBaseSkills(sections: SkillSection[], filters: BaseFilters): SkillCatalogSkill[] {
  const normalizedQuery = filters.query.trim().toLowerCase()
  return sections
    .flatMap(section => section.skills)
    .filter(skill => passesBaseGates(skill, filters, normalizedQuery))
}

// Order values by their position in a canonical list (the registry order plumbed
// from op-llm-skills through the server). Values absent from the canonical list —
// e.g. a proprietary-only category — keep alphabetical order at the end so they
// stay visible. With no canonical order, fall back to plain alphabetical.
function orderByCanonical(values: string[], canonicalOrder: string[]): string[] {
  if (!canonicalOrder.length) {
    return [...values].sort((left, right) => left.localeCompare(right))
  }

  const rank = new Map(canonicalOrder.map((value, index) => [value, index]))
  const known = values
    .filter(value => rank.has(value))
    .sort((left, right) => (rank.get(left) ?? 0) - (rank.get(right) ?? 0))
  const unknown = values
    .filter(value => !rank.has(value))
    .sort((left, right) => left.localeCompare(right))

  return [...known, ...unknown]
}

function collectFacetsFromSkills(skills: SkillCatalogSkill[], categoryOrder: string[]): SkillFacets {
  const roles = new Set<string>()
  const categories = new Set<string>()

  skills.forEach(skill => {
    skill.roles?.forEach(roleTag => roles.add(roleTag))
    skill.categories?.forEach(categoryTag => categories.add(categoryTag))
  })

  return {
    roles: [...roles].sort((left, right) => left.localeCompare(right)),
    categories: orderByCanonical([...categories], categoryOrder),
  }
}

export function collectSkillFacets(sections: SkillSection[], categoryOrder: string[] = []): SkillFacets {
  return collectFacetsFromSkills(sections.flatMap(section => section.skills), categoryOrder)
}

// The facet values worth showing as chips: only those carried by a skill that is
// currently in scope (tab + search + preview). A value whose only skills are gated
// out — e.g. a category held solely by a hidden beta skill — drops out here.
export function getAvailableFacets(
  sections: SkillSection[],
  filters: BaseFilters,
  categoryOrder: string[] = [],
): SkillFacets {
  return collectFacetsFromSkills(getBaseSkills(sections, filters), categoryOrder)
}

// Whether the "Show preview workflows" switch is worth offering: is there any beta
// skill in the current tab/search scope, regardless of the switch's own state?
export function hasPreviewableSkills(
  sections: SkillSection[],
  { tab, query }: Pick<SkillsLibraryFilters, "tab" | "query">,
): boolean {
  return getBaseSkills(sections, { tab, query, showPreview: true }).some(skill => skill.beta)
}
